// src/worker/cover-check.js
//
// Cover check (v132.97, docs/cover-check-plan.md): "with the staff we have and
// this week's rota, are the right people in the right place at the right time?"
//
// PURE maths, ZERO dependencies. Imported by the Worker (config route, the area
// view), the staff-app rota bundle and the AI rota generator, so the store
// view, the area view and the generator can never disagree (the same pattern
// as BADGES in achievements.js). Keep it dependency-free.
//
// Day index: 0 = Sunday ... 6 = Saturday (JS getDay order), which is also the
// column order of the Cubes "sales by hour" report.

export const COVER_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const CONFIG_KEY = 'cover_check';
// Sales per person on the floor per hour. The defaults are the values that
// give the same total shifts as the rotas of May to September 2026, placed
// where the sales are (the 29 Sep 2026 rota-against-trade analysis).
export const DEFAULT_TARGET = { UAE: 468, Qatar: 728 };
export const COUNTRIES = ['UAE', 'Qatar'];
const TARGET_MIN = 50, TARGET_MAX = 5000;

// 'HH:MM' (or 'H:MM', or 'HH:MM:SS') to minutes after midnight; '24:00' is 1440.
export function timeToMinutes(t) {
  if (t == null) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t).trim());
  if (!m) return null;
  const h = Number(m[1]), mi = Number(m[2]);
  if (h > 24 || mi > 59 || (h === 24 && mi !== 0)) return null;
  return h * 60 + mi;
}

// A store's opening and closing time for one day, from the opening_hours
// rows (current season). A close at or before the open (a time input can only
// say '00:00' for midnight) means after midnight. Falls back to `fallback`
// ({open, close} in minutes) when the store has no usable row.
//
// `source` NAMES WHICH GAP IT FELL THROUGH, because the three need different
// actions and the screen should not send a manager to the wrong one:
//   'store'          the store's own times for this season. Nothing to do.
//   'default_season' the store HAS hours, but not for the season now active.
//                    Found 1 Oct 2026: the v82 seed carried Ramadan rows for
//                    only 12 of the 29 stores, so flipping the season to
//                    ramadan drops the other 17 (all seven Qatar stores, nine
//                    Dubai stores and Al Reem Mall) through to the default.
//                    They need RAMADAN hours entered, not hours.
//   'default_day'    a row for this season exists but that day is blank.
//   'default'        no hours at all for this store, in any season.
// A season row is matched on an exact season value, so a row whose season is
// 'regular' does NOT serve a 'ramadan' lookup. That is deliberate: Ramadan
// hours genuinely differ, and quietly serving the regular ones would look
// authoritative while being wrong, which is worse than a named default.
export function openingHoursFor(rows, store, season, dayIdx, fallback) {
  const key = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][dayIdx];
  const list = Array.isArray(rows) ? rows : [];
  const forStore = list.filter((r) => r && r.store === store);
  const row = forStore.find((r) => season == null || r.season === season)
    || (season == null ? null : forStore.find((r) => !r.season));
  if (row) {
    const open = timeToMinutes(row[key + '_open']);
    let close = timeToMinutes(row[key + '_close']);
    if (open != null && close != null) {
      if (close <= open) close += 1440;
      return { open, close, source: 'store' };
    }
  }
  if (!fallback) return null;
  const source = row ? 'default_day' : (forStore.length ? 'default_season' : 'default');
  return { open: fallback.open, close: fallback.close, source };
}

// Which active stores have no hours row for `season`. The answer to "what
// breaks if I flip the season now?", so the flip can say it before it happens
// rather than leaving it to be discovered store by store.
export function storesMissingSeasonHours(rows, stores, season) {
  const list = Array.isArray(rows) ? rows : [];
  return (Array.isArray(stores) ? stores : [])
    .filter((name) => !list.some((r) => r && r.store === name && r.season === season));
}

// The Cubes "sales by hour" export as an array of rows (SheetJS
// sheet_to_json with header: 1). Row 1 holds the weekday names, each above a
// "Sales w/o VAT" column; data rows start with the hour (0 to 23). Returns
// { sales: 7 x 24 numbers, total } or throws an Error saying what is wrong.
export function parseCubesHourly(aoa) {
  const rows = Array.isArray(aoa) ? aoa : [];
  const names = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  let headerRow = -1, cols = null;
  for (let r = 0; r < Math.min(rows.length, 10) && headerRow < 0; r++) {
    const row = rows[r] || [];
    const found = names.map((n) => row.findIndex((c) => String(c == null ? '' : c).trim().toLowerCase() === n));
    if (found.every((i) => i >= 0)) { headerRow = r; cols = found; }
  }
  if (headerRow < 0) throw new Error('No row with the seven weekday names (Sunday to Saturday) was found. Use the Cubes "Sales by Hour" report with weekdays across the top.');
  const sales = COVER_DAYS.map(() => new Array(24).fill(0));
  let total = 0, hoursSeen = 0;
  for (let r = headerRow + 1; r < rows.length; r++) {
    const row = rows[r] || [];
    const first = row[0];
    if (first == null || String(first).trim() === '') continue;
    const h = Number(first);
    if (!Number.isInteger(h) || h < 0 || h > 23) continue;
    hoursSeen++;
    cols.forEach((c, d) => {
      const v = Number(row[c]);
      if (Number.isFinite(v) && v > 0) { sales[d][h] += v; total += v; }
    });
  }
  if (!hoursSeen || !(total > 0)) throw new Error('No hourly sales were found under the weekday headings.');
  return { sales, total };
}

// Each weekday's hourly sales as shares of that day (each day sums to 1).
export function sharesFromSales(sales) {
  return COVER_DAYS.map((_, d) => {
    const day = (sales && sales[d]) || [];
    const t = day.reduce((a, b) => a + (Number(b) || 0), 0);
    return new Array(24).fill(0).map((_, h) => (t > 0 ? Math.round(((Number(day[h]) || 0) / t) * 1e6) / 1e6 : 0));
  });
}

function validPattern(p) {
  if (!Array.isArray(p) || p.length !== 7) return false;
  return p.every((day) => Array.isArray(day) && day.length === 24 && day.every((v) => Number.isFinite(v) && v >= 0 && v <= 1)
    && Math.abs(day.reduce((a, b) => a + b, 0) - 1) < 0.01);
}

// Validate a settings payload. A country's pattern may be missing (that
// country is then "not set up"); anything present must be well formed.
export function validateConfig(input) {
  const errors = [];
  const src = input && typeof input === 'object' ? input : {};
  const config = { target: {}, pattern: {}, source: {} };
  COUNTRIES.forEach((c) => {
    const t = src.target && src.target[c] != null ? Number(src.target[c]) : DEFAULT_TARGET[c];
    if (!Number.isFinite(t) || t < TARGET_MIN || t > TARGET_MAX) errors.push(c + ': sales per person must be between ' + TARGET_MIN + ' and ' + TARGET_MAX + '.');
    else config.target[c] = Math.round(t);
    const p = src.pattern && src.pattern[c];
    if (p != null) {
      if (!validPattern(p)) errors.push(c + ': the hourly pattern must be 7 days of 24 shares, each day adding up to 1.');
      else config.pattern[c] = p.map((day) => day.map((v) => Math.round(v * 1e6) / 1e6));
    }
    const s = src.source && src.source[c];
    if (s != null) config.source[c] = String(s).slice(0, 200);
  });
  return { ok: errors.length === 0, errors, config };
}

export function isConfigured(config, country) {
  return !!(config && config.pattern && validPattern(config.pattern[country]) && config.target && config.target[country] > 0);
}

// The whole hours a store is open, as hour numbers (may run past 23 for a
// store open after midnight: 24 is 12am to 1am).
export function openHours(open, close) {
  const out = [];
  if (open == null || close == null || close <= open) return out;
  for (let h = Math.floor(open / 60); h * 60 < close; h++) out.push(h);
  return out;
}

// People needed on the floor each open hour: the day's sales spread by the
// country's hourly pattern (re-scaled to the hours the store is open),
// divided by sales per person, rounded up, never fewer than one.
export function neededByHour({ daySales, shares, open, close, target }) {
  const hours = openHours(open, close);
  if (!hours.length) return [];
  const w = hours.map((h) => (shares && Number.isFinite(shares[h % 24]) ? shares[h % 24] : 0));
  const ws = w.reduce((a, b) => a + b, 0);
  const sales = Number(daySales) > 0 ? Number(daySales) : 0;
  return hours.map((h, i) => {
    const hourSales = sales > 0 ? (ws > 0 ? sales * w[i] / ws : sales / hours.length) : 0;
    const need = target > 0 && hourSales > 0 ? Math.max(1, Math.ceil(hourSales / target - 0.1)) : 1;
    return { hour: h, sales: Math.round(hourSales), need };
  });
}

// People on the floor each hour from a day's work shifts
// ({ start, end, lunchAt, lunchMins }). An hour holding a lunch counts only
// the part of the hour not on lunch. An end at or before the start is after
// midnight.
export function rosteredByHour(shifts, hours) {
  const spans = (Array.isArray(shifts) ? shifts : []).map((s) => {
    const a = timeToMinutes(s && s.start);
    let b = timeToMinutes(s && s.end);
    if (a == null || b == null) return null;
    if (b <= a) b += 1440;
    const la = timeToMinutes(s.lunchAt);
    const lm = Number(s.lunchMins) || 0;
    let lunch = null;
    if (la != null && lm > 0) { let l0 = la; if (l0 < a) l0 += 1440; lunch = [l0, l0 + lm]; }
    return { a, b, lunch };
  }).filter(Boolean);
  const overlap = (x0, x1, y0, y1) => Math.max(0, Math.min(x1, y1) - Math.max(x0, y0));
  return (hours || []).map((h) => {
    const h0 = h * 60, h1 = h0 + 60;
    let mins = 0;
    spans.forEach((s) => {
      let m = overlap(s.a, s.b, h0, h1);
      if (m && s.lunch) m -= overlap(s.lunch[0], s.lunch[1], Math.max(s.a, h0), Math.min(s.b, h1));
      mins += Math.max(0, m);
    });
    return Math.round((mins / 60) * 100) / 100;
  });
}

export function formatHour(h) {
  const x = ((h % 24) + 24) % 24;
  return x === 0 ? '12am' : x === 12 ? '12pm' : x < 12 ? x + 'am' : (x - 12) + 'pm';
}

// Compare one day's need with what is rostered. Hours short by at least a
// quarter of a person count as short (a lunch overlapping by a few minutes is
// not a gap). Returns short and spare person-hours and the short stretches.
export function compareDay(needed, rostered) {
  let short = 0, spare = 0;
  const hours = needed.map((n, i) => {
    const have = Number(rostered[i]) || 0;
    const gap = have - n.need;
    if (gap <= -0.25) short += -gap;
    if (gap >= 0.25) spare += gap;
    return { hour: n.hour, need: n.need, have, gap: Math.round(gap * 100) / 100 };
  });
  const runs = [];
  hours.forEach((x) => {
    const isShort = x.gap <= -0.25;
    const last = runs[runs.length - 1];
    if (isShort && last && last.to === x.hour) { last.to = x.hour + 1; last.missing += -x.gap; }
    else if (isShort) runs.push({ from: x.hour, to: x.hour + 1, missing: -x.gap });
  });
  return { short: Math.round(short * 10) / 10, spare: Math.round(spare * 10) / 10, hours, runs };
}

// Advice for one day (never a block). `needed` and `cmp` from the functions
// above; `shifts` the day's work shifts.
export function dayWarnings(needed, cmp, shifts) {
  const out = [];
  const eveningShort = cmp.hours.filter((x) => x.hour >= 18 && x.need >= 2 && x.have <= 1.25);
  if (eveningShort.length) out.push('only one person on the floor after 6pm when ' + Math.max(...eveningShort.map((x) => x.need)) + ' are needed');
  const starts = (Array.isArray(shifts) ? shifts : []).map((s) => timeToMinutes(s && s.start)).filter((v) => v != null).sort((a, b) => a - b);
  const morningNeed = needed.filter((n) => n.hour < 12).reduce((m, n) => Math.max(m, n.need), 0);
  if (starts.length >= 2 && starts[1] < 12 * 60 && morningNeed <= 1) out.push('a second person starts before 12pm but the morning only needs one');
  return out;
}

// The week's headline: short and spare hours and the biggest short stretches.
export function weekSummary(days) {
  const list = Array.isArray(days) ? days : [];
  const short = Math.round(list.reduce((a, d) => a + (d.cmp ? d.cmp.short : 0), 0) * 10) / 10;
  const spare = Math.round(list.reduce((a, d) => a + (d.cmp ? d.cmp.spare : 0), 0) * 10) / 10;
  const runs = [];
  list.forEach((d) => (d.cmp ? d.cmp.runs : []).forEach((r) => runs.push({ day: d.day, from: r.from, to: r.to, missing: r.missing })));
  runs.sort((a, b) => b.missing - a.missing);
  return { short, spare, runs };
}

export function verdictText(summary) {
  if (!summary) return '';
  const top = summary.runs.slice(0, 2).map((r) => COVER_DAYS[r.day] + ' ' + formatHour(r.from) + ' to ' + formatHour(r.to)).join(', ');
  const s = summary.short >= 0.5 ? Math.round(summary.short) + ' hours short' + (top ? ' (' + top + ')' : '') : 'no hours short';
  const p = summary.spare >= 0.5 ? Math.round(summary.spare) + ' hours spare' : 'no spare hours';
  return 'This week: ' + s + ', ' + p + '.';
}

// A day's sales for the check: the target when there is one, otherwise the
// average actual sales on the same weekday over the 8 weeks before `date`
// (zero days ignored). `history` is [{ date: 'YYYY-MM-DD', sales }].
export function daySalesFor(date, target, history) {
  if (Number(target) > 0) return { value: Number(target), source: 'target' };
  const d0 = new Date(date + 'T00:00:00Z');
  if (isNaN(d0)) return { value: 0, source: 'none' };
  const dow = d0.getUTCDay(), from = d0.getTime() - 56 * 86400000;
  const vals = (Array.isArray(history) ? history : []).filter((r) => {
    const t = new Date(String(r.date).slice(0, 10) + 'T00:00:00Z');
    return !isNaN(t) && t.getUTCDay() === dow && t.getTime() >= from && t.getTime() < d0.getTime() && Number(r.sales) > 0;
  }).map((r) => Number(r.sales));
  if (!vals.length) return { value: 0, source: 'none' };
  return { value: Math.round(vals.reduce((a, b) => a + b, 0) / vals.length), source: 'history' };
}

// Today's rota defaults for a store with no opening hours of its own:
// open 9am, close 10pm, midnight on Friday and Saturday (the same times as
// STORE_CLOSE_BY_DAY in rota.js, which the staff app uses).
export function defaultHours(dayIdx) {
  return { open: 540, close: (dayIdx === 5 || dayIdx === 6) ? 1440 : 1320 };
}

function addDaysIso(dateStr, n) {
  const d = new Date(dateStr + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

// The area view (Stage 3): every store in scope, this week, short and spare
// by day, plus same-day suggestions inside an area (one store with a spare
// person, another short). Pure: the route loads the rows and passes them in.
//   weekMonday: 'YYYY-MM-DD' (a Monday; Qatar stores use the Sunday before)
//   stores:  [{ name, area, region: 'UAE'|'Qatar' }]
//   staff:   [{ id, store }]
//   shifts:  [{ staff_id, week_start, day, type, start_time, end_time, lunch_at, lunch_mins, work_store }]
//   targets: [{ date, store, value }]   sales: [{ date, store, sales }]
//   hoursRows: opening_hours rows; config: readCoverConfig() output
export function buildAreaCover({ weekMonday, stores, staff, shifts, targets, sales, hoursRows, config }) {
  const homeOf = {};
  (staff || []).forEach((s) => { if (s && s.id != null) homeOf[String(s.id)] = s.store; });
  const targetOf = {};
  (targets || []).forEach((t) => { if (t && t.store && t.date) targetOf[t.store + '|' + String(t.date).slice(0, 10)] = Number(t.value); });
  const salesBy = {};
  (sales || []).forEach((r) => { if (r && r.store) (salesBy[r.store] = salesBy[r.store] || []).push({ date: String(r.date).slice(0, 10), sales: r.sales }); });
  const shiftsBy = {};   // store|date -> [shift]
  (shifts || []).forEach((r) => {
    if (!r || r.type !== 'work' || !r.start_time || !r.end_time) return;
    const store = r.work_store || homeOf[String(r.staff_id)];
    if (!store) return;
    const idx = COVER_DAYS.indexOf(r.day);
    if (idx < 0) return;
    const wsDow = new Date(String(r.week_start).slice(0, 10) + 'T12:00:00Z').getUTCDay();
    const date = addDaysIso(String(r.week_start).slice(0, 10), (idx - wsDow + 7) % 7);
    (shiftsBy[store + '|' + date] = shiftsBy[store + '|' + date] || []).push({ start: r.start_time, end: r.end_time, lunchAt: r.lunch_at, lunchMins: r.lunch_mins });
  });
  const out = (stores || []).filter((s) => s && s.name && (s.region === 'UAE' || s.region === 'Qatar')).map((s) => {
    const configured = isConfigured(config, s.region);
    const weekStart = s.region === 'Qatar' ? addDaysIso(weekMonday, -1) : weekMonday;
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = addDaysIso(weekStart, i);
      const dayIdx = new Date(date + 'T12:00:00Z').getUTCDay();
      const dayShifts = shiftsBy[s.name + '|' + date] || [];
      if (!configured) { days.push({ date, day: COVER_DAYS[dayIdx], hasRota: dayShifts.length > 0 }); continue; }
      const hours = openingHoursFor(hoursRows, s.name, config.season, dayIdx, defaultHours(dayIdx));
      const ds = daySalesFor(date, targetOf[s.name + '|' + date], salesBy[s.name]);
      const needed = neededByHour({ daySales: ds.value, shares: config.pattern[s.region][dayIdx], open: hours.open, close: hours.close, target: config.target[s.region] });
      const cmp = compareDay(needed, rosteredByHour(dayShifts, needed.map((n) => n.hour)));
      days.push({ date, day: COVER_DAYS[dayIdx], hasRota: dayShifts.length > 0, short: cmp.short, spare: cmp.spare, runs: cmp.runs, sales: ds.value, salesSource: ds.source, shifts: dayShifts.length });
    }
    const rota = days.filter((d) => d.hasRota);
    return {
      store: s.name, area: s.area || '', region: s.region, weekStart, configured, days,
      short: Math.round(rota.reduce((a, d) => a + (d.short || 0), 0) * 10) / 10,
      spare: Math.round(rota.reduce((a, d) => a + (d.spare || 0), 0) * 10) / 10,
    };
  });
  // Same-day suggestions inside an area: a store with a whole spare shift's
  // worth of people-hours (9 or more) beside one short by 3 hours or more.
  const suggestions = [];
  const areas = {};
  out.forEach((s) => { (areas[s.area] = areas[s.area] || []).push(s); });
  Object.keys(areas).forEach((area) => {
    const list = areas[area];
    const dates = {};
    list.forEach((s) => s.days.forEach((d) => { if (d.hasRota) (dates[d.date] = dates[d.date] || []).push({ s, d }); }));
    Object.keys(dates).sort().forEach((date) => {
      const donors = dates[date].filter((x) => x.d.spare >= 9).sort((a, b) => b.d.spare - a.d.spare);
      const takers = dates[date].filter((x) => x.d.short >= 3).sort((a, b) => b.d.short - a.d.short);
      takers.forEach((t, i) => {
        const giver = donors[i];
        if (!giver) return;
        const run = t.d.runs && t.d.runs[0];
        suggestions.push({
          area, date, day: t.d.day, from: giver.s.store, to: t.s.store, spare: giver.d.spare, short: t.d.short,
          when: run ? formatHour(run.from) + ' to ' + formatHour(run.to) : '',
        });
      });
    });
  });
  return { weekMonday, stores: out, suggestions };
}

// ── Stage 4 (v132.100): what the AI rota generator is told ─────────────────

const pad2 = (n) => String(n).padStart(2, '0');
// Minutes to 'HH:MM' for the rota (midnight is '24:00'; later closes clamp to it).
export function minutesToRotaTime(m) {
  const x = Math.min(1440, Math.max(0, Math.round(Number(m) || 0)));
  return pad2(Math.floor(x / 60)) + ':' + pad2(x % 60);
}

// The fewest 9-hour shifts that give at least the needed people every open
// hour: at the first short hour start as many shifts as are short, as late as
// the close allows. Returns start times in minutes, earliest first.
export function suggestShifts(needed, open, close) {
  const hours = needed.map((n) => n.hour);
  if (!hours.length) return [];
  const cov = hours.map(() => 0), starts = [];
  const lastStart = Math.max(open, close - 540);
  hours.forEach((h, i) => {
    for (let k = needed[i].need - cov[i]; k > 0; k--) {
      const s = Math.max(open, Math.min(h * 60, lastStart));
      starts.push(s);
      hours.forEach((hh, j) => { if (hh * 60 >= s && hh * 60 < s + 540) cov[j]++; });
    }
  });
  return starts.sort((a, b) => a - b);
}

// "2 from 9am, 3 from 11am, 4 from 5pm, 2 from 10pm"
export function needBands(needed) {
  const out = [];
  needed.forEach((n) => { if (!out.length || out[out.length - 1].need !== n.need) out.push({ from: n.hour, need: n.need }); });
  return out.map((b) => b.need + ' from ' + formatHour(b.from)).join(', ');
}

// Everything the generator needs for one store and week, or null when the
// store's country is not set up (the generator then behaves exactly as before).
//   dates: the week's 'YYYY-MM-DD' dates (any order); targetsByDate: { date: value }
export function generatorContext({ store, country, dates, config, hoursRows, targetsByDate, history }) {
  if (!isConfigured(config, country)) return null;
  const days = (dates || []).map((date) => {
    const dayIdx = new Date(date + 'T12:00:00Z').getUTCDay();
    const hours = openingHoursFor(hoursRows, store, config.season, dayIdx, defaultHours(dayIdx));
    const sales = daySalesFor(date, targetsByDate && targetsByDate[date], history);
    const needed = neededByHour({ daySales: sales.value, shares: config.pattern[country][dayIdx], open: hours.open, close: hours.close, target: config.target[country] });
    return { date, dayIdx, day: COVER_DAYS[dayIdx], hours, sales, needed, starts: suggestShifts(needed, hours.open, hours.close) };
  });
  const byDay = {};
  days.forEach((d) => { byDay[d.day] = d; });
  const hoursByDay = {}, targetsByDay = {};
  days.forEach((d) => {
    hoursByDay[d.day] = { open: minutesToRotaTime(d.hours.open), close: minutesToRotaTime(d.hours.close), source: d.hours.source };
    targetsByDay[d.day] = d.sales.value;
  });
  const withSales = days.filter((d) => d.sales.value > 0).sort((a, b) => a.sales.value - b.sales.value);
  const quietDays = withSales.length >= 5 ? withSales.slice(0, 3).map((d) => d.day) : null;
  const busyDays = withSales.length >= 5 ? withSales.slice(-2).map((d) => d.day) : null;
  const coverLines = days.map((d) => '  ' + d.day + ' (open ' + minutesToRotaTime(d.hours.open) + '-' + minutesToRotaTime(d.hours.close) + '): ' +
    needBands(d.needed) + '. Suggested shifts: ' + (d.starts.length ? d.starts.map((s) => minutesToRotaTime(s) + '-' + minutesToRotaTime(Math.min(s + 540, d.hours.close))).join(', ') : 'none'));
  return { country, byDay, hoursByDay, targetsByDay, quietDays, busyDays, coverLines };
}

// People needed at a minute of a day, for the lunch optimiser (0 when closed).
export function needAtFn(gctx, day) {
  const d = gctx && gctx.byDay && gctx.byDay[day];
  if (!d) return null;
  return (minute) => {
    const x = d.needed.find((n) => n.hour === Math.floor(minute / 60));
    return x ? x.need : 0;
  };
}

// Advice on a finished draft ({ day, start, finish, lunchAt, lunch } shifts):
// short stretches and the day warnings, one line each.
export function coverWarnings(workShifts, gctx) {
  if (!gctx) return [];
  const out = [];
  COVER_DAYS.forEach((day) => {
    const d = gctx.byDay[day];
    if (!d) return;
    const shifts = (workShifts || []).filter((s) => s && s.day === day && s.start && s.finish)
      .map((s) => ({ start: s.start, end: s.finish, lunchAt: s.lunchAt, lunchMins: s.lunch }));
    if (!shifts.length) return;
    const cmp = compareDay(d.needed, rosteredByHour(shifts, d.needed.map((n) => n.hour)));
    cmp.runs.forEach((r) => {
      const worst = cmp.hours.filter((x) => x.hour >= r.from && x.hour < r.to).sort((a, b) => a.gap - b.gap)[0];
      out.push(day + ' ' + formatHour(r.from) + ' to ' + formatHour(r.to) + ': ' + (Math.round(worst.have * 10) / 10) + ' on the floor, ' + worst.need + ' needed');
    });
    dayWarnings(d.needed, cmp, shifts).forEach((w) => out.push(day + ': ' + w));
  });
  return out;
}
